import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Compass,
  ShieldCheck,
  MapPin,
  Clock,
  FileSignature,
  Mountain,
  ClipboardList,
  Handshake,
} from "lucide-react";
import Footer from "@/components/site/Footer";
import Navbar from "@/components/site/Navbar";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import StickyCTA from "@/components/site/StickyCTA";
import { useReveal } from "@/lib/reveal";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format&fit=crop";

const expertise = [
  {
    icon: FileSignature,
    title: "Permis de construire",
    desc: "Dossier complet prêt à être déposé en mairie.",
  },
  {
    icon: Mountain,
    title: "Études de sol",
    desc: "Missions G1 à G5 réalisées avec des partenaires spécialisés.",
  },
  {
    icon: ClipboardList,
    title: "Dossiers techniques & administratifs",
    desc: "Pièces, CERFA, notices, suivi d'instruction.",
  },
  {
    icon: Handshake,
    title: "Accompagnement complet",
    desc: "Un seul interlocuteur, de A à Z, jusqu'à l'obtention du permis.",
  },
];

const engagements = [
  { icon: Compass, text: "Un accompagnement de A à Z" },
  { icon: ShieldCheck, text: "Un projet sécurisé dès le départ" },
  { icon: MapPin, text: "Une expertise adaptée à La Réunion" },
  { icon: Clock, text: "Un gain de temps considérable" },
];

export default function QuiSommesNous() {
  useReveal();

  useEffect(() => {
    document.title = "Qui sommes-nous ? — Solmis Habitat";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-foreground min-h-screen">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden pt-36 md:pt-44 pb-20 md:pb-28">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-40 left-0 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--brand-terra)/0.08),transparent_70%)]" />
          </div>

          <div className="container-wide relative grid gap-16 md:grid-cols-12 items-end">
            <div className="md:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" />
                <span className="overline">À propos</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-[4.5rem] font-medium leading-[1.05] text-primary">
                Qui sommes-{" "}
                <span className="italic font-light text-[hsl(var(--brand-terra))]">
                  nous
                </span>{" "}
                ?
              </h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl font-light text-primary/85 leading-snug">
                Un partenaire local pour vos projets de construction à La
                Réunion, du sondage de terrain au dépôt en mairie.
              </p>
            </div>

            <div className="md:col-span-5 reveal">
              <div className="relative aspect-[4/5] overflow-hidden border border-border">
                <img
                  src={ABOUT_IMG}
                  alt="Architecture moderne — La Réunion"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute -top-4 -left-4 h-20 w-20 border-t border-l border-[hsl(var(--brand-terra))]" />
                <div className="absolute -bottom-4 -right-4 h-20 w-20 border-b border-r border-[hsl(var(--brand-terra))]" />
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-narrow">
            <div className="grid md:grid-cols-12 gap-12">
              <div className="md:col-span-5 reveal">
                <div className="flex items-center gap-3 mb-5">
                  <span className="rule" />
                  <span className="overline">Notre histoire</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                  Construire peut vite devenir{" "}
                  <span className="italic font-light text-[hsl(var(--brand-terra))]">
                    complexe
                  </span>
                  .
                </h2>
              </div>

              <div className="md:col-span-7 reveal space-y-5 text-base md:text-lg leading-relaxed text-primary/85">
                <p>
                  <strong>Solmis Habitat</strong> accompagne les particuliers
                  et professionnels à La Réunion dans leurs projets de
                  construction, de l'étude de sol à l'obtention du permis de
                  construire.
                </p>
                <p>
                  Notre objectif est simple : vous offrir un accompagnement
                  <strong> complet, clair et sécurisé</strong>, avec un seul
                  interlocuteur à chaque étape de votre projet.
                </p>
                <p className="text-muted-foreground">
                  Construire peut vite devenir complexe : contraintes
                  techniques, démarches administratives, risques d'erreurs…
                  C'est pourquoi nous avons conçu une approche globale pour
                  simplifier votre projet et vous faire gagner du temps.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Expertise */}
        <section className="py-20 md:py-28">
          <div className="container-wide">
            <div className="max-w-3xl mb-14 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">Notre expertise</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Quatre pôles{" "}
                <span className="italic font-light">d'intervention</span>.
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {expertise.map((e, i) => {
                const Icon = e.icon;
                return (
                  <article
                    key={e.title}
                    className="reveal border border-border bg-white p-8 hover:-translate-y-1 transition-all duration-300 hover:border-[hsl(var(--brand-terra))]"
                    style={{ transitionDelay: `${i * 70}ms` }}
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-6 font-display text-xl text-primary leading-snug">
                      {e.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {e.desc}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partner network */}
        <section className="py-20 md:py-28 bg-primary text-primary-foreground">
          <div className="container-narrow">
            <div className="grid md:grid-cols-12 gap-10 items-start reveal">
              <div className="md:col-span-5">
                <div className="flex items-center gap-3 mb-5">
                  <span className="block h-px w-10 bg-[hsl(var(--brand-terra))]" />
                  <span className="text-xs font-semibold uppercase tracking-[0.24em] text-[hsl(var(--brand-terra))]">
                    Réseau d'experts
                  </span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl leading-tight">
                  Un{" "}
                  <span className="italic font-light text-white/85">
                    réseau de partenaires
                  </span>{" "}
                  experts.
                </h2>
              </div>
              <div className="md:col-span-7 space-y-5 text-base md:text-lg leading-relaxed text-white/85">
                <p>
                  Pour garantir un haut niveau de qualité technique, Solmis
                  Habitat s'appuie sur des partenaires spécialisés en
                  ingénierie et études de sol.
                </p>
                <p className="text-white/65">
                  Cette collaboration nous permet de vous proposer des
                  solutions fiables, conformes aux exigences locales et
                  adaptées à votre terrain.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Commitments */}
        <section className="py-20 md:py-28">
          <div className="container-wide">
            <div className="max-w-3xl mb-14 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">Notre engagement</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Quatre promesses{" "}
                <span className="italic font-light">tenues</span>.
              </h2>
            </div>

            <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-4 border border-border">
              {engagements.map((e, i) => {
                const Icon = e.icon;
                return (
                  <div
                    key={e.text}
                    className="reveal bg-white p-8 md:p-10"
                    style={{ transitionDelay: `${i * 70}ms` }}
                  >
                    <CheckCircle2
                      size={20}
                      strokeWidth={1.6}
                      className="text-[hsl(var(--brand-terra))]"
                    />
                    <p className="mt-5 font-display text-xl md:text-2xl text-primary leading-snug">
                      {e.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mission + CTA */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-narrow text-center reveal">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="rule" />
              <span className="overline">Notre mission</span>
              <span className="rule" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-primary leading-[1.08] max-w-3xl mx-auto">
              Vous permettre de concrétiser votre projet{" "}
              <span className="italic font-light text-[hsl(var(--brand-terra))]">
                sereinement
              </span>
              , sans erreur et sans perte de temps.
            </h2>

            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link
                to="/#contact"
                data-testid="about-cta-devis"
                className="group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
              >
                Demander mon devis gratuit
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/"
                data-testid="about-back"
                className="inline-flex items-center gap-2 border border-primary text-primary px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:bg-primary hover:text-primary-foreground"
              >
                <ArrowLeft size={16} strokeWidth={1.6} />
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}
